/**
 * "What's new" changelog data — OMN-368.
 *
 * The LIVE content is a JSON file in a public Supabase Storage bucket
 * (`public-content/whats-new.json`), fetched at runtime. That's what lets us
 * publish a new entry WITHOUT re-releasing the app — edit the canonical JSON and
 * run `scripts/whats-new/publish.mjs` (see that script's header).
 *
 * The canonical JSON (`./whats-new.json`) is committed for git history/review
 * AND bundled here as the OFFLINE FALLBACK: if the runtime fetch fails (offline,
 * PWA, Storage hiccup) the app still shows the last-built entries. Live content
 * always wins when the fetch succeeds.
 *
 * Voice: customer-facing, benefit-led. Tags are exactly: 'new' | 'improved' | 'fixed'.
 */
import { supabase } from '../../lib/supabaseClient';
import { logger } from '../../utils/logger';
import fallbackDoc from './whats-new.json';

export type ChangelogTag = 'new' | 'improved' | 'fixed';

/** Per-locale strings; `en` is required and is the fallback for any missing locale. */
export type LocalizedText = { en: string } & Partial<Record<string, string>>;

export interface ChangelogEntry {
  id: string;
  /** ISO date 'YYYY-MM-DD'. Lexicographic order === chronological order. */
  date: string;
  tag: ChangelogTag;
  title: LocalizedText;
  body: LocalizedText;
}

/** Resolve a localized field to the current language, falling back to English. */
export function pickLocale(text: LocalizedText, lang: string): string {
  return text[lang] ?? text.en;
}

const BUCKET = 'public-content';
const OBJECT = 'whats-new.json';
const VALID_TAGS: ReadonlySet<string> = new Set(['new', 'improved', 'fixed']);

/** A localized field must be an object with at least a non-empty `en` string. */
function isLocalized(v: unknown): v is LocalizedText {
  return !!v && typeof v === 'object' && typeof (v as Record<string, unknown>).en === 'string'
    && ((v as Record<string, string>).en).trim() !== '';
}

/** Newest first; ignores anything malformed rather than throwing. */
function normalize(entries: unknown): ChangelogEntry[] {
  if (!Array.isArray(entries)) return [];
  const clean = entries.filter((e): e is ChangelogEntry => {
    if (!e || typeof e !== 'object') return false;
    const c = e as Record<string, unknown>;
    return (
      typeof c.id === 'string' &&
      typeof c.date === 'string' &&
      typeof c.tag === 'string' &&
      VALID_TAGS.has(c.tag) &&
      isLocalized(c.title) &&
      isLocalized(c.body)
    );
  });
  return clean.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}

/** Bundled fallback entries (from the committed JSON), newest first. */
export function getFallbackEntries(): ChangelogEntry[] {
  return normalize((fallbackDoc as { entries?: unknown }).entries);
}

/**
 * Fetch the live changelog from public Storage. Returns null on ANY problem so
 * callers fall back to the bundled copy. `cache: 'no-cache'` revalidates (etag)
 * so a freshly published file shows up without waiting on an HTTP cache.
 */
export async function fetchRemoteEntries(): Promise<ChangelogEntry[] | null> {
  try {
    const { data } = supabase.storage.from(BUCKET).getPublicUrl(OBJECT);
    const res = await fetch(data.publicUrl, { cache: 'no-cache' });
    if (!res.ok) return null;
    const doc = await res.json();
    const entries = normalize(doc?.entries);
    return entries.length > 0 ? entries : null;
  } catch (err) {
    logger.warn('[WhatsNew] remote changelog fetch failed; using bundled fallback', err);
    return null;
  }
}
